import {
  computeRenewalPriceCents,
  type CouponMetadataLike,
  type RenewalPlanLike,
} from './computeRenewalPriceCents';

export interface RenewalCouponBreakdown {
  planPriceCents: number;
  couponDiscountCents: number;
  renewalPriceCents: number;
  code: string | null;
  /** Renewals the discount still covers; -1 means every renewal (lifetime). */
  cyclesLeft: number;
}

/** Plan price vs. coupon-discounted renewal price, or null when no discount applies. */
export function describeRenewalCoupon({
  plan,
  paymentRecurrency,
  couponMetadata,
}: {
  plan: RenewalPlanLike | null | undefined;
  paymentRecurrency: string | null | undefined;
  couponMetadata: CouponMetadataLike | null | undefined;
}): RenewalCouponBreakdown | null {
  if (!plan || !couponMetadata) return null;
  const recurrency = paymentRecurrency || 'monthly';
  const cycles = Number(
    recurrency === 'yearly' ? couponMetadata.year_cycles : couponMetadata.month_cycles,
  );
  if (!Number.isFinite(cycles) || (cycles !== -1 && cycles <= 0)) return null;

  const planPriceCents = computeRenewalPriceCents({
    plan,
    paymentRecurrency: recurrency,
    couponMetadata: null,
  });
  const renewalPriceCents = computeRenewalPriceCents({
    plan,
    paymentRecurrency: recurrency,
    couponMetadata,
  });
  const couponDiscountCents = planPriceCents - renewalPriceCents;
  if (couponDiscountCents <= 0) return null;

  return {
    planPriceCents,
    couponDiscountCents,
    renewalPriceCents,
    code: couponMetadata.code ?? null,
    cyclesLeft: cycles,
  };
}
